import { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import {
  Patient,
  AudioRecording,
  PatientSharedNote,
  TestPhoto,
  addPatientSharedNote,
  updatePatient,
  updateAudioTranscription,
  uploadNeurologistAudio,
} from "../api/patients";
import { useAuth } from "../auth/AuthContext";
import TestPhotosInline from "./TestPhotosInline";

interface Props {
  patient: Patient;
  onPatientUpdated: (patient: Patient) => void;
}

function playBeep(duration = 300, frequency = 800, volume = 0.3) {
  const ctx = new AudioContext();
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = frequency;
  gain.gain.value = volume;
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start();
  setTimeout(() => {
    oscillator.stop();
    ctx.close();
  }, duration);
}

function formatTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", { timeZone: "Asia/Almaty" });
}

type RecordingState = "idle" | "recording" | "uploading" | "transcribing";

function EditableTranscription({
  rec,
  audioType,
  patientId,
  onUpdated,
}: {
  rec: AudioRecording;
  audioType: "animals" | "story_retell";
  patientId: string;
  onUpdated: (result: { transcription: string; cluster_analysis?: any }) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(rec.transcription || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Sync if rec changes externally
  useEffect(() => { setText(rec.transcription || ""); }, [rec.transcription]);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    setError("");
    try {
      const result = await updateAudioTranscription(patientId, audioType, rec.sequence, trimmed);
      onUpdated(result);
      setEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pl: 4.5, pb: 0.5, width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          Распознанный текст:
        </Typography>
        {!editing && (
          <Button size="small" sx={{ minWidth: 0, p: 0.3 }} onClick={() => setEditing(true)}>
            <EditIcon sx={{ fontSize: 14 }} />
          </Button>
        )}
      </Box>

      {editing ? (
        <Box sx={{ mt: 0.25 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            size="small"
            sx={{ bgcolor: "white" }}
          />
          {error && <Typography variant="caption" color="error">{error}</Typography>}
          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 14 }} />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Пересчёт..." : "Сохранить"}
            </Button>
            <Button size="small" variant="outlined" onClick={() => { setEditing(false); setText(rec.transcription || ""); }} disabled={saving}>
              Отмена
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ mt: 0.25, bgcolor: "white", p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
          {rec.transcription}
        </Typography>
      )}

      {rec.cluster_analysis && (
        <Box sx={{ mt: 0.5 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
            <Chip label={`${rec.cluster_analysis.total_unique} уник. животных`} size="small" color="primary" variant="outlined" />
            <Chip label={`${rec.cluster_analysis.cluster_count} кластеров`} size="small" color="secondary" variant="outlined" />
            <Chip label={`${rec.cluster_analysis.switches} переключений`} size="small" variant="outlined" />
            {rec.cluster_analysis.repetitions.length > 0 && (
              <Chip label={`Повторы: ${rec.cluster_analysis.repetitions.join(", ")}`} size="small" color="warning" variant="outlined" />
            )}
          </Box>
          {rec.cluster_analysis.clusters.map((cl: any, ci: number) => (
            <Box key={ci} sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {cl.name}:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {cl.animals.join(", ")}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

const STT_LANGUAGES: { value: string; label: string }[] = [
  { value: "RU", label: "Русский" },
  { value: "KK", label: "Қазақша" },
];

function AudioRecordSection({
  label,
  description,
  durationSec,
  audioType,
  patientId,
  patientLanguage,
  recordings,
  onRecorded,
  onUpdateRecording,
}: {
  label: string;
  description: string;
  durationSec: number;
  audioType: "animals" | "story_retell";
  patientId: string;
  patientLanguage: string;
  recordings: AudioRecording[];
  onRecorded: (rec: AudioRecording) => void;
  onUpdateRecording: (index: number, rec: AudioRecording) => void;
}) {
  const [state, setState] = useState<RecordingState>("idle");
  const [sttLang, setSttLang] = useState(patientLanguage?.toUpperCase() || "RU");
  const [timeLeft, setTimeLeft] = useState(durationSec);
  const [error, setError] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
    playBeep(500, 600, 0.4);
  }, []);

  const startRecording = async () => {
    setError("");
    chunksRef.current = [];
    setTimeLeft(durationSec);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });

        setState("uploading");
        try {
          // After short delay switch to transcribing status
          const transcribeTimer = setTimeout(() => setState("transcribing"), 2000);

          const result = await uploadNeurologistAudio(
            patientId,
            audioType,
            blob,
            sttLang
          );
          clearTimeout(transcribeTimer);

          onRecorded({
            url: result.file_url,
            sequence: result.sequence,
            uploaded_by: "",
            uploaded_by_email: result.uploaded_by_email,
            uploaded_at: new Date().toISOString(),
            transcription: result.transcription,
            cluster_analysis: result.cluster_analysis,
            stt_language: sttLang,
          });
          setState("idle");
          setTimeLeft(durationSec);
        } catch (err: any) {
          setError(err?.response?.data?.detail || "Ошибка загрузки");
          setState("idle");
        }
      };

      mediaRecorder.start(1000);
      setState("recording");
      playBeep();

      let remaining = durationSec;
      timerRef.current = setInterval(() => {
        remaining -= 1;
        setTimeLeft(remaining);
        if (remaining <= 0) {
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      setError(
        "Нет доступа к микрофону. Разрешите доступ в настройках браузера."
      );
    }
  };

  const progress = ((durationSec - timeLeft) / durationSec) * 100;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderColor: state === "recording" ? "error.main" : "divider",
        borderWidth: state === "recording" ? 2 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        {recordings.length > 0 && (
          <Chip
            label={`${recordings.length} зап.`}
            color="primary"
            size="small"
            variant="outlined"
          />
        )}
        <Chip
          label="записано в кабинете"
          size="small"
          variant="outlined"
          sx={{ color: "text.secondary" }}
        />
      </Box>

      {/* List of existing recordings */}
      {recordings.length > 0 && (
        <List dense sx={{ mb: 1, bgcolor: "grey.50", borderRadius: 1 }}>
          {recordings.map((rec, i) => (
            <ListItem key={i} sx={{ flexDirection: "column", alignItems: "flex-start" }}>
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AudioFileIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={`Запись #${rec.sequence}`}
                  secondary={`${formatDateTime(rec.uploaded_at)}${rec.uploaded_by_email ? ` — ${rec.uploaded_by_email}` : ""}${rec.stt_language ? ` · ${STT_LANGUAGES.find(l => l.value === rec.stt_language)?.label || rec.stt_language}` : ""}`}
                />
              </Box>
              {rec.transcription && (
                <EditableTranscription
                  rec={rec}
                  audioType={audioType}
                  patientId={patientId}
                  onUpdated={(updated) => {
                    const newRec = { ...rec, transcription: updated.transcription, cluster_analysis: updated.cluster_analysis ?? rec.cluster_analysis };
                    onUpdateRecording(i, newRec);
                  }}
                />
              )}
            </ListItem>
          ))}
        </List>
      )}

      {/* Recording UI */}
      {state === "recording" && (
        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: "error.main",
                  animation: "pulse 1.5s infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.3 },
                  },
                }}
              />
              <Typography variant="body2" color="error">
                Запись...
              </Typography>
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, fontFamily: "monospace" }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            color="error"
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {state === "idle" && (
        <Alert severity="info" icon={false} sx={{ mb: 1, py: 0.5 }}>
          Распознавание будет на языке: <strong>{STT_LANGUAGES.find(l => l.value === sttLang)?.label || sttLang}</strong>
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        {state === "idle" && (
          <>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={sttLang}
                onChange={(e) => setSttLang(e.target.value)}
              >
                {STT_LANGUAGES.map((l) => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant={recordings.length > 0 ? "outlined" : "contained"}
              color="primary"
              startIcon={<MicIcon />}
              onClick={startRecording}
              fullWidth
            >
              {recordings.length > 0
                ? `Новая запись (${formatTime(durationSec)})`
                : `Начать запись (${formatTime(durationSec)})`}
            </Button>
          </>
        )}

        {state === "recording" && (
          <Button
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            onClick={stopRecording}
            fullWidth
          >
            Остановить запись
          </Button>
        )}

        {state === "uploading" && (
          <Button variant="outlined" disabled fullWidth>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Обрабатываем аудио... Извлекаем признаки...
          </Button>
        )}

        {state === "transcribing" && (
          <Button variant="outlined" disabled fullWidth>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Распознаём речь...
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export default function NeurologistAssessment({
  patient,
  onPatientUpdated,
}: Props) {
  const { user } = useAuth();
  const canCreateNotes = Boolean(user);

  const [mocaScore, setMocaScore] = useState<string>(
    patient.moca_score?.toString() || ""
  );
  const [qmciScore, setQmciScore] = useState<string>(
    patient.qmci_score?.toString() || ""
  );
  const [depressionScore, setDepressionScore] = useState<string>(
    patient.depression_score?.toString() || ""
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [animalsRecs, setAnimalsRecs] = useState<AudioRecording[]>(
    patient.neurologist_audio_animals || []
  );
  const [storyRecs, setStoryRecs] = useState<AudioRecording[]>(
    patient.neurologist_audio_story_retell || []
  );
  const [mocaPhotos, setMocaPhotos] = useState<TestPhoto[]>(
    patient.test_photos_moca || []
  );
  const [qmciPhotos, setQmciPhotos] = useState<TestPhoto[]>(
    patient.test_photos_qmci || []
  );
  const [depressionPhotos, setDepressionPhotos] = useState<TestPhoto[]>(
    patient.test_photos_depression || []
  );
  const [sharedNotes, setSharedNotes] = useState<PatientSharedNote[]>(
    (patient.shared_notes || []).slice().sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  );
  const [newNoteText, setNewNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [noteSuccess, setNoteSuccess] = useState(false);

  useEffect(() => {
    if (saveSuccess) {
      const t = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [saveSuccess]);

  useEffect(() => {
    if (noteSuccess) {
      const t = setTimeout(() => setNoteSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [noteSuccess]);

  useEffect(() => {
    setMocaScore(patient.moca_score?.toString() || "");
    setQmciScore(patient.qmci_score?.toString() || "");
    setDepressionScore(patient.depression_score?.toString() || "");
    setAnimalsRecs(patient.neurologist_audio_animals || []);
    setStoryRecs(patient.neurologist_audio_story_retell || []);
    setMocaPhotos(patient.test_photos_moca || []);
    setQmciPhotos(patient.test_photos_qmci || []);
    setDepressionPhotos(patient.test_photos_depression || []);
    setSharedNotes(
      (patient.shared_notes || []).slice().sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    );
    setNewNoteText("");
    setNoteError("");
    setNoteSuccess(false);
  }, [patient]);

  const handleSaveScores = async () => {
    setSaveError("");
    setSaving(true);
    try {
      const data: { moca_score?: number; qmci_score?: number; depression_score?: number } = {};
      if (mocaScore) data.moca_score = parseFloat(mocaScore);
      if (qmciScore) data.qmci_score = parseFloat(qmciScore);
      if (depressionScore) data.depression_score = parseFloat(depressionScore);

      if (Object.keys(data).length === 0) {
        setSaveError("Введите хотя бы один балл");
        setSaving(false);
        return;
      }

      const updated = await updatePatient(patient.patient_id!, data);
      onPatientUpdated(updated);
      setSaveSuccess(true);
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSharedNote = async () => {
    setNoteError("");
    setNoteSaving(true);
    try {
      const trimmed = newNoteText.trim();
      if (!trimmed) {
        setNoteError("Введите текст заметки");
        setNoteSaving(false);
        return;
      }

      const created = await addPatientSharedNote(patient.patient_id!, trimmed);
      const updatedNotes = [created, ...sharedNotes].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setSharedNotes(updatedNotes);
      setNewNoteText("");
      setNoteSuccess(true);
      onPatientUpdated({ ...patient, shared_notes: updatedNotes });
    } catch (err: any) {
      setNoteError(err?.response?.data?.detail || "Ошибка добавления заметки");
    } finally {
      setNoteSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Оценка невролога
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Результаты тестирования
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="MoCA"
            type="number"
            value={mocaScore}
            onChange={(e) => setMocaScore(e.target.value)}
            size="small"
            slotProps={{ htmlInput: { min: 0, max: 30, step: 1 } }}
            helperText="0-30 баллов"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Qmci"
            type="number"
            value={qmciScore}
            onChange={(e) => setQmciScore(e.target.value)}
            size="small"
            slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
            helperText="0-100 баллов"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Depression"
            type="number"
            value={depressionScore}
            onChange={(e) => setDepressionScore(e.target.value)}
            size="small"
            slotProps={{ htmlInput: { min: 0, max: 10000, step: 1 } }}
            sx={{ flex: 1 }}
          />
        </Box>

        {saveError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {saveError}
          </Alert>
        )}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 1 }}>
            Баллы сохранены
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSaveScores}
          disabled={saving}
          sx={{ mb: 3 }}
        >
          Сохранить баллы
        </Button>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Фото тестов
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              MoCA
            </Typography>
            <TestPhotosInline
              patientId={patient.patient_id!}
              testType="moca"
              photos={mocaPhotos}
              onPhotosUpdated={(photos) => {
                setMocaPhotos(photos);
                onPatientUpdated({ ...patient, test_photos_moca: photos });
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              Qmci
            </Typography>
            <TestPhotosInline
              patientId={patient.patient_id!}
              testType="qmci"
              photos={qmciPhotos}
              onPhotosUpdated={(photos) => {
                setQmciPhotos(photos);
                onPatientUpdated({ ...patient, test_photos_qmci: photos });
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              Depression
            </Typography>
            <TestPhotosInline
              patientId={patient.patient_id!}
              testType="depression"
              photos={depressionPhotos}
              onPhotosUpdated={(photos) => {
                setDepressionPhotos(photos);
                onPatientUpdated({ ...patient, test_photos_depression: photos });
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Заметки по пациенту
        </Typography>

        {canCreateNotes && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 2 }}>
            <TextField
              label="Новая заметка"
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={noteSaving ? <CircularProgress size={18} /> : <SaveIcon />}
              onClick={handleAddSharedNote}
              disabled={noteSaving}
              sx={{ minWidth: 130, mt: 0.5 }}
            >
              Внести
            </Button>
          </Box>
        )}

        {noteError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {noteError}
          </Alert>
        )}
        {noteSuccess && (
          <Alert severity="success" sx={{ mb: 1 }}>
            Заметка добавлена
          </Alert>
        )}

        <TableContainer sx={{ bgcolor: "grey.50", borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 190 }}>Время</TableCell>
                <TableCell sx={{ width: 260 }}>Автор</TableCell>
                <TableCell>Текст заметки</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sharedNotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                    Пока нет заметок
                  </TableCell>
                </TableRow>
              ) : (
                sharedNotes.map((note) => {
                  const author = note.author_name?.trim()
                    || note.author_email?.trim()
                    || "Неизвестный автор";
                  return (
                    <TableRow key={note.note_id}>
                      <TableCell>{formatDateTime(note.created_at)}</TableCell>
                      <TableCell>{author}</TableCell>
                      <TableCell sx={{ whiteSpace: "pre-wrap" }}>{note.text}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <AudioRecordSection
          label="Запись - Перечисление животных"
          description="Задание №5: участник называет максимальное количество животных за 60 секунд"
          durationSec={60}
          audioType="animals"
          patientId={patient.patient_id!}
          patientLanguage={patient.language || "RU"}
          recordings={animalsRecs}
          onRecorded={(rec) => setAnimalsRecs((prev) => [...prev, rec])}
          onUpdateRecording={(idx, rec) => setAnimalsRecs((prev) => { const a = [...prev]; a[idx] = rec; return a; })}
        />

        <AudioRecordSection
          label="Запись - Пересказ истории"
          description="Задание №6: участник пересказывает историю за 30 секунд"
          durationSec={30}
          audioType="story_retell"
          patientId={patient.patient_id!}
          patientLanguage={patient.language || "RU"}
          recordings={storyRecs}
          onRecorded={(rec) => setStoryRecs((prev) => [...prev, rec])}
          onUpdateRecording={(idx, rec) => setStoryRecs((prev) => { const a = [...prev]; a[idx] = rec; return a; })}
        />
      </Box>
    </Box>
  );
}
